import { ErrorLevel } from '@abstractTypes/message'
import { LogData, LogFunction } from '@abstractTypes/utils'
import config from '@config/index'
import debug from 'debug'
import store from '../store'

const BASE = 'digital-wall-app'

const COLOURS: Record<ErrorLevel, string> = {
  trace: 'lightblue',
  info: 'blue',
  warn: 'orange',
  error: 'red',
}

/** Generate a debug message */
const generateMessage = (level: ErrorLevel, message?: ErrorInput, source?: string) => {
  // Set the prefix which will cause debug to enable the message
  const namespace = `${BASE}:${level}`
  const createDebug = debug(namespace)

  // send log to api server for level > of trace...
  if (['trace', 'info'].indexOf(level) === -1 && config.log2api) {
    const state = store.getState()
    const msg = typeof message === 'string' ? message : message?.message || ''
    const body = JSON.stringify({
      level,
      // Message sometime is an error object with message attribute
      msg: `React app - ${source} - ${msg}`,
    })
    const headers = {
      'dw-session-id': state.session.token,
      'Content-Type': 'application/json',
    }

    fetch(config.logServerUrl, {
      method: 'POST',
      body,
      headers,
    })
  }

  if (createDebug.enabled) {
    // Set the colour of the message based on the level
    createDebug.color = COLOURS[level]

    if (source) {
      createDebug(source, message)
    } else {
      createDebug(message)
    }
  }
}

/**
 * This class is used to log messages to browser console
 * and to backend service...
 *
 * How to use:
 *  import Log from '@libs/libs/Log'
 *  Log.info('AAA', 'app.js');
 *
 * To enable logging is necessary to add an entry 'debug'
 * on browser's localStorage with this value:
 *
 *  digital-wall-app:<level|*>
 *
 * Level should be a value such as: trace | info | warn | error | *
 * in order to enable a specific logging level.
 *
 * further info: https://levelup.gitconnected.com/step-up-your-console-messaging-game-in-your-react-app-42eee17659ec
 */

type ErrorInput = { message: string } | string

export class Log {
  onLog: LogFunction | undefined

  constructor(onLog: LogFunction | undefined) {
    this.onLog = onLog
  }

  static trace(message?: ErrorInput, source?: string) {
    return generateMessage('trace', message, source)
  }

  static info(message?: ErrorInput, source?: string) {
    return generateMessage('info', message, source)
  }

  static warn(message?: ErrorInput, source?: string) {
    return generateMessage('warn', message, source)
  }

  static error(message?: ErrorInput, source?: string) {
    return generateMessage('error', message, source)
  }

  log(logData: LogData) {
    typeof this.onLog === 'function' ? this.onLog(logData) : undefined
  }
}

export default function (onLog: LogFunction | undefined): Log {
  return new Log(onLog)
}
